import type { BlockGuide } from "./types";

export const guide: BlockGuide = {
  slug: "snapchat",
  app: "Snapchat",
  title: "How to block Snapchat on iPhone — and the streak problem",
  h1: "How to block Snapchat",
  description:
    "Screen Time steps, why a streak is a social debt rather than a game, and how to leave without the number becoming a reason to stay.",
  targets: [
    "how to block snapchat on iphone",
    "block snapchat app",
    "how to quit snapchat",
    "snapchat streaks quitting",
  ],
  published: "2026-09-03",
  standfirst:
    "Nobody stays on Snapchat for the content. They stay because leaving costs something a number is keeping track of.",
  takeaways: [
    "The streak is the mechanic and it is a social obligation, not a game. Breaking one is felt by another person, which is why the app is unusually hard to leave.",
    "Tell the two or three people first. A streak ended without warning reads as a friendship ending; a streak ended with a message does not.",
    "Because it is a messaging app, a total block usually fails. The workable version removes the streak pressure rather than the app.",
  ],
  mechanic: [
    "Snapchat's central mechanic is not a feed. It is a counter next to another person's name that goes up if you both send something every day and resets to zero if either of you does not. That is a debt with a public balance, and the person on the other end can see it.",
    "This makes leaving Snapchat structurally different from leaving any other app on this list. Deleting TikTok costs you nothing that anybody else notices. Letting a three-hundred-day streak lapse is a message sent to a friend whether or not you intended to send one, and teenagers in particular read it exactly that way — which is why people hand their login to a friend before going on holiday, a behaviour no other app produces.",
    "Around that sits the ephemerality. Snapchat's content disappears, so there is no archive to catch up on later and no state of having seen it — if you were not there, it is gone. That converts checking from something you do when free into something you do before it expires, which is a far more urgent kind of pull.",
  ],
  limitFails:
    "Snapchat is a messaging app for most of the people who use it, so a hard block gets carved out with an exception within about a week, and once there is an exception there is no limit. The deeper failure is that Screen Time is measuring the wrong thing entirely: the streak does not care how many minutes you spend, only that you appear once a day, so a person can satisfy every limit on the phone and still be organising their evenings around an app they wanted to leave.",
  instead: [
    "Message the two or three people whose streaks you actually care about and tell them you are stopping. That converts a silent signal into a conversation and removes most of the cost.",
    "Turn off all notifications except direct messages, and turn off the ones that tell you a streak is about to expire — those exist to produce exactly the panic they describe.",
    "Move the conversations that matter to a messaging app with no counter attached.",
    "Then delete the app rather than limiting it. Snapchat is one of the few here where the middle option does not work.",
  ],
  verses: [
    {
      verse: {
        text: '"All things are lawful for me," but not all things are expedient. "All things are lawful for me," but I will not be brought under the power of anything.',
        ref: "1 Corinthians 6:12",
      },
      note: "The test Paul applies is not whether a thing is forbidden but whether it has power over him, which is the only question worth asking about a streak. Nobody thinks a counter is a sin. Plenty of people have handed a friend their password so a number would not reset while they were away, and that is what being brought under the power of something looks like.",
    },
    {
      verse: {
        text: "A friend loves at all times; and a brother is born for adversity.",
        ref: "Proverbs 17:17",
      },
      note: "At all times is the phrase that does the work, and it is a claim about constancy rather than contact. A friendship measured by daily proof is not being kept warm by the measuring; it is being converted into an obligation, and the proverb's version of friendship is the one that survives a fortnight of silence.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "A streak is a debt",
      id: "a-streak-is-a-debt",
    },
    {
      type: "p",
      text: "Loss aversion is the most reliable finding in behavioural economics: people work considerably harder to avoid losing something than to gain something equivalent. A streak turns an app into a thing you can lose, every single day, forever.",
    },
    {
      type: "p",
      text: "And unlike a game's streak, this one has a witness. That is the difference, and it is why the usual advice about deleting the app lands so badly with the people who most need it.",
    },
    {
      type: "h2",
      text: "Say it out loud first",
      id: "say-it-out-loud-first",
    },
    {
      type: "p",
      text: "The single most effective step is a message: I am coming off Snapchat, it is not about you, here is where to find me. Sent to the three people it applies to, it costs a minute and removes the entire social penalty.",
    },
    {
      type: "note",
      text: "Almost nobody does this, and almost everybody who does reports that the other person was relieved. The obligation runs in both directions.",
    },
    {
      type: "h2",
      text: "For parents",
      id: "for-parents",
    },
    {
      type: "p",
      text: "If this is about a child rather than yourself, the streak is the part to understand before you take the phone away. Removing the app without warning breaks several friendships from their point of view, and the resulting fight will be about the friendships rather than about screen time.",
    },
    {
      type: "p",
      text: "The version that works is agreed in advance and gives them the chance to tell people — the same principle as {every other rule about phones in a house}.",
      links: {
        "every other rule about phones in a house":
          "/blog/phone-rules-for-christian-families",
      },
    },
  ],
  faq: [
    {
      q: "How do I block Snapchat on an iPhone?",
      a: "Screen Time → App Limits → Social Networking, with Block at End of Limit and a passcode. But for Snapchat specifically, deleting the app after telling your streak partners works far better than a limit.",
    },
    {
      q: "What happens to my streaks if I stop using Snapchat?",
      a: "They reset to zero, and the other person sees that. Which is why the useful first step is a message telling them you are stopping — it turns a silent signal into a conversation.",
    },
    {
      q: "Why is Snapchat so hard to quit?",
      a: "Because the streak is a social obligation with a public counter, and loss aversion is far stronger than attraction. You are not staying for the content; you are avoiding a loss another person can see.",
    },
  ],
  relatedApps: ["instagram", "discord", "whatsapp"],
  related: ["phone-rules-for-christian-families", "social-media-fast-guide"],
  relatedPrayers: ["prayer-for-self-control", "prayer-for-loneliness"],
};
